import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, LoaderCircleIcon, SendHorizontalIcon, SparklesIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { NisoloSubmission, SizeTier } from '../hooks/useNisoloSubmissions';
import { NisoloOrderData } from '../hooks/useNisoloOrders';
import { LineOutcome, reconcileSubmission } from '../utils/submissionOutcome';
import { askCarli, AgentMessage } from '../utils/carliAgent';
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const compact = (value: number) => Math.abs(value) >= 1000 ? `${value < 0 ? '−' : ''}$${Math.round(Math.abs(value) / 1000).toLocaleString('en-US')}K` : currency(value);
const perUnit = (value: number) => `$${value.toFixed(2)}`;
const count = (value: number) => value.toLocaleString('en-US');
const signedPct = (value: number) => `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}%`;
export function UploadAIInsights({
  uploads,
  orders,
  loading,
  error
}: {
  uploads: NisoloSubmission[];
  orders: NisoloOrderData | null;
  loading: boolean;
  error: string | null;
}) {
  const [openUpload, setOpenUpload] = useState<string | null>(null);
  if (loading) {
    return <article className="flex items-center justify-center gap-2 rounded-2xl border border-ink/10 bg-paper px-6 py-12 text-sm text-ink/55" data-id="element-2025" data-mp="f1dacex6efur">
        <LoaderCircleIcon className="h-4 w-4 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-2026" data-mp="3ighp2j1twkq9" />
        Carli is reading the receiving file…
      </article>;
  }
  if (error) {
    return <article className="rounded-2xl border border-ink/10 bg-paper px-6 py-12 text-center text-sm text-ink/55" data-id="element-2027" data-mp="3rgo0lzdyvams">{error}</article>;
  }
  if (uploads.length === 0) {
    return <article className="rounded-2xl border border-ink/10 bg-paper p-8 text-center" aria-labelledby="ai-insights-empty" data-id="element-2028" data-mp="7qmxp77l46d">
        <h2 id="ai-insights-empty" className="text-base font-semibold" data-id="element-2029" data-mp="1boqn16zssnay">AI insights</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/55" data-id="element-2030" data-mp="37sen1cqo4fmc">Select an inventory submission above to see Carli’s read on what she predicted and what actually happened.</p>
      </article>;
  }
  return <div className="space-y-4" data-id="element-2031" data-mp="3hqudddhvfkus">
      {uploads.map(upload => <InsightReport key={upload.id} upload={upload} orders={orders} open={openUpload === upload.id} onToggle={() => setOpenUpload(current => current === upload.id ? null : upload.id)} data-id="element-2032" data-mp="3crhpqqj6e9r5" />)}
    </div>;
}
function InsightReport({
  upload,
  orders,
  open,
  onToggle
}: {
  upload: NisoloSubmission;
  orders: NisoloOrderData | null;
  open: boolean;
  onToggle: () => void;
}) {
  const outcome = useMemo(() => reconcileSubmission(upload, orders), [upload, orders]);
  const weightedMsrp = upload.received > 0 ? upload.msrp / upload.received : 0;
  const coreShare = upload.received > 0 ? Math.round(upload.sizes.filter(size => size.tier === 'Core').reduce((sum, size) => sum + size.units, 0) / upload.received * 100) : 0;
  const headlineTone = outcome.matched ? outcome.variance >= 0 ? 'text-forest' : 'text-[#94452E]' : 'text-ink';
  return <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" data-id="element-2033" data-mp="7u5878cqsm1a">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-cream/45 sm:px-6" data-id="element-2034" data-mp="buxjs893730q">
        <span className="flex min-w-0 items-center gap-3" data-id="element-2035" data-mp="14itcl1vjcas8">
          <CarliMark data-id="element-2036" data-mp="qk3f3l4t093c" />
          <span className="min-w-0" data-id="element-2037" data-mp="qkmqeizy7z8e">
            <span className="block text-base font-semibold text-ink" data-id="element-2038" data-mp="qjl9hxw94iir">AI insights · {upload.name}</span>
            <span className="mt-0.5 block text-xs text-ink/50" data-id="element-2039" data-mp="vhd2kv8muaoj">
              {upload.grade} · {count(upload.received)} units · predicted {compact(upload.estimatedRecovery)}
              {outcome.matched && ` · actual ${compact(outcome.actualSales)} to date`}
            </span>
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-3" data-id="element-2040" data-mp="3vl53762bj18r">
          {outcome.matched ? <span className={`hidden items-center gap-1 text-xs font-semibold sm:flex ${headlineTone}`} data-id="element-2041" data-mp="3qr0w8mt1yt7o">
              {outcome.variance >= 0 ? <TrendingUpIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2042" data-mp="3hrnhpfq2neg3" /> : <TrendingDownIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2043" data-mp="3hti7b7cafuqi" />}
              {signedPct(outcome.variancePct)} vs predicted
            </span> : <span className="hidden rounded-full bg-ink/8 px-2.5 py-1 text-[11px] font-semibold text-ink/55 sm:inline" data-id="element-2044" data-mp="deieplvefy2c">Awaiting first sales</span>}
          <ChevronDownIcon className={`h-4 w-4 text-ink/45 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2045" data-mp="4cr7udp4hl6w" />
        </span>
      </button>

      {open && <div className="border-t border-ink/10 px-5 py-6 sm:px-6" data-id="element-2046" data-mp="2tzqbc45e5emd">
          <Scoreboard upload={upload} outcome={outcome} data-id="element-2047" data-mp="2p1f13sb6p23m" />

          <CarliNarrative upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2048" data-mp="14lh5qti4ogmw" />

          {outcome.matched && (outcome.overPerformers.length > 0 || outcome.underPerformers.length > 0) && <section className="mt-4 grid gap-3 lg:grid-cols-2" aria-label="Where the model was right and wrong" data-id="element-2049" data-mp="1dku6mxs6rww0">
              <VarianceList title="Beat the prediction" tone="up" items={outcome.overPerformers} empty="Nothing has cleared above its estimate yet." data-id="element-2050" data-mp="1idx25bgixgeu" />
              <VarianceList title="Missed the prediction" tone="down" items={outcome.underPerformers} empty="Nothing has cleared below its estimate yet." data-id="element-2051" data-mp="1sccwroerf7t3" />
            </section>}

          <Composition upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2052" data-mp="2x6gc4d9m7cl" />

          <ReportChat upload={upload} outcome={outcome} weightedMsrp={weightedMsrp} coreShare={coreShare} data-id="element-2053" data-mp="6xguw5f4i632" />
        </div>}
    </article>;
}
function Scoreboard({
  upload,
  outcome
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
}) {
  const positive = outcome.variance >= 0;
  return <section aria-label="Predicted versus actual" data-id="element-2054" data-mp="1f77ap7i8womu">
      <div className="grid gap-3 lg:grid-cols-[1.1fr_1.1fr_0.8fr]" data-id="element-2055" data-mp="1f6ecww19l9gu">
        <article className="rounded-xl border border-ink/10 bg-cream/45 p-5" data-id="element-2056" data-mp="1t6fbu5xyy6to">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2057" data-mp="1o7vqo7r78z2d">Predicted recovery</p>
          <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-2058" data-mp="22wifr16mp56s">{currency(upload.estimatedRecovery)}</p>
          <p className="mt-1.5 text-xs text-ink/55" data-id="element-2059" data-mp="1xyqcxc4g3ynt">{perUnit(outcome.predictedAur)} per unit across {count(upload.received)} units</p>
        </article>

        <article className={`rounded-xl border p-5 ${outcome.matched ? positive ? 'border-forest/30 bg-forest/6' : 'border-[#BB5E40]/30 bg-[#BB5E40]/6' : 'border-ink/10 bg-cream/45'}`} data-id="element-2060" data-mp="2bv3sugtbzs06">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2061" data-mp="2qi2hy54puabq">Actual recovery to date</p>
          <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${outcome.matched ? positive ? 'text-forest' : 'text-[#94452E]' : 'text-ink/40'}`} data-id="element-2062" data-mp="2vgvxs47h6df2">
            {outcome.matched ? currency(outcome.actualSales) : '—'}
          </p>
          <p className="mt-1.5 text-xs text-ink/55" data-id="element-2063" data-mp="27smfhc9h07so">
            {outcome.matched ? `${perUnit(outcome.actualAur)} per unit across ${count(outcome.soldUnits)} units sold` : 'No matched sales recorded against this upload yet'}
          </p>
        </article>

        <article className="rounded-xl border border-ink/10 bg-paper p-5" data-id="element-2064" data-mp="22tt3xpdquqv5">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink/50" data-id="element-2065" data-mp="1ov1rs8ifzf9n">Variance on sold units</p>
          {outcome.matched ? <>
              <p className={`mt-2 font-display text-3xl font-semibold tracking-tight ${positive ? 'text-forest' : 'text-[#94452E]'}`} data-id="element-2066" data-mp="1tt3agsbyhckl">{signedPct(outcome.variancePct)}</p>
              <p className="mt-1.5 text-xs text-ink/55" data-id="element-2067" data-mp="24dungjgkfqhk">{compact(outcome.variance)} against {currency(outcome.predictedForSold)} predicted for those units</p>
            </> : <>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink/40" data-id="element-2068" data-mp="1vb3cekrlqwqg">—</p>
              <p className="mt-1.5 text-xs text-ink/55" data-id="element-2069" data-mp="1qdto58fy8r47">Variance appears once units from this upload sell</p>
            </>}
        </article>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3" data-id="element-2070" data-mp="1gmyzjxf8dffl">
        <MiniStat label="Sell-through" value={`${outcome.sellThrough.toFixed(1)}%`} detail={`${count(outcome.soldUnits)} of ${count(upload.received)} units`} data-id="element-2071" data-mp="1gni84nizqrgy" />
        <MiniStat label="Units remaining" value={count(outcome.unsoldUnits)} detail={`${compact(outcome.remainingPredicted)} still forecast`} data-id="element-2072" data-mp="17mts5na1eh1f" />
        <MiniStat label="AUR vs forecast" value={outcome.matched ? signedPct(outcome.aurVariancePct) : '—'} detail={outcome.matched ? `${perUnit(outcome.actualAur)} actual vs ${perUnit(outcome.predictedAur)} predicted` : `${perUnit(outcome.predictedAur)} predicted`} tone={outcome.matched ? outcome.aurVariancePct >= 0 ? 'up' : 'down' : undefined} data-id="element-2073" data-mp="12o90ic8up5ol" />
      </div>

      <div className="mt-3" data-id="element-2074" data-mp="3s87x9uwf8i3b">
        <div className="flex items-center justify-between text-xs text-ink/55" data-id="element-2075" data-mp="2vkuvhxvc7v1w">
          <span data-id="element-2076" data-mp="2zlwmxsj0j2tb">Recovered {compact(outcome.actualSales)}</span>
          <span data-id="element-2077" data-mp="2zmg7lh01s5b1">Forecast {compact(upload.estimatedRecovery)}</span>
        </div>
        <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-ink/8" role="img" aria-label={`${Math.round(outcome.actualSales / Math.max(1, upload.estimatedRecovery) * 100)}% of forecast recovered`} data-id="element-2078" data-mp="34jydeyirq4et">
          <div className="h-full rounded-full bg-[#BB5E40]" style={{
          width: `${Math.min(100, outcome.actualSales / Math.max(1, upload.estimatedRecovery) * 100)}%`
        }} data-id="element-2079" data-mp="2bvsumhb9yphm" />
        </div>
      </div>
    </section>;
}
function MiniStat({
  label,
  value,
  detail,
  tone
}: {
  label: string;
  value: string;
  detail: string;
  tone?: 'up' | 'down';
}) {
  return <article className="rounded-xl border border-ink/10 bg-paper px-4 py-3.5" data-id="element-2080" data-mp="2th3pcelbuf37">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2081" data-mp="2tf11a03qiw14">{label}</p>
      <p className={`mt-1.5 font-display text-xl font-semibold tracking-tight ${tone === 'up' ? 'text-forest' : tone === 'down' ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-2082" data-mp="2ydudzqb76j08">{value}</p>
      <p className="mt-0.5 text-xs text-ink/50" data-id="element-2083" data-mp="25rsvzi6b3mz3">{detail}</p>
    </article>;
}
function VarianceList({
  title,
  tone,
  items,
  empty
}: {
  title: string;
  tone: 'up' | 'down';
  items: LineOutcome[];
  empty: string;
}) {
  const up = tone === 'up';
  return <article className="rounded-xl border border-ink/10 bg-paper p-5" data-id="element-2084" data-mp="1i1yjj1pwg0fd">
      <div className="flex items-center gap-2" data-id="element-2085" data-mp="1hzkrl1qin4c1">
        {up ? <TrendingUpIcon className="h-4 w-4 text-forest" aria-hidden="true" data-id="element-2086" data-mp="1mzqc7xydbqkm" /> : <TrendingDownIcon className="h-4 w-4 text-[#94452E]" aria-hidden="true" data-id="element-2087" data-mp="1fgz63laxz6cz" />}
        <h4 className="text-sm font-semibold" data-id="element-2088" data-mp="1fewgv3hm5h9b">{title}</h4>
      </div>
      {items.length === 0 ? <p className="mt-3 text-xs text-ink/50" data-id="element-2089" data-mp="15j2xp19e2pik">{empty}</p> : <ul className="mt-3 space-y-2.5" data-id="element-2090" data-mp="15h8crmy4axeh">
          {items.map(item => <li key={item.name} className="flex items-center justify-between gap-3 border-b border-ink/8 pb-2.5 last:border-0 last:pb-0" data-id="element-2091" data-mp="10j6siyoygs1f">
              <span className="min-w-0" data-id="element-2092" data-mp="wk7snsi2zlgu">
                <span className="block truncate text-xs font-medium text-ink" data-id="element-2093" data-mp="rrel6wv89z12">{item.name}</span>
                <span className="text-[11px] text-ink/50" data-id="element-2094" data-mp="mrsaov6sawvy">
                  {count(item.soldUnits)} of {count(item.receivedUnits)} sold · {perUnit(item.actualAur)} actual vs {perUnit(item.predict)} predicted
                </span>
              </span>
              <span className={`shrink-0 text-xs font-semibold tabular-nums ${up ? 'text-forest' : 'text-[#94452E]'}`} data-id="element-2095" data-mp="cwi5jyl3a9dy">
                {item.variance >= 0 ? '+' : '−'}{currency(Math.abs(item.variance))}
              </span>
            </li>)}
        </ul>}
    </article>;
}
function CarliNarrative({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState('');
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSummary('');
    setFailed('');
    askCarli({
      messages: [{
        role: 'user',
        content: 'Give me your read on this upload: what you predicted, what actually happened, why, and what to do next.'
      }],
      context: buildContext(upload, outcome, weightedMsrp, coreShare, ['Write exactly three short labelled paragraphs, each on its own line, in this format:', 'CALL: what the model predicted and the reasoning behind it, in one or two sentences.', 'RESULT: what actually happened versus that prediction, naming the specific drivers.', 'NEXT: the single most valuable action to take now, stated concretely.', 'No preamble, no bullet points, no headings other than those three labels.']),
      maxTokens: 500
    }).then(reply => {
      if (!cancelled) setSummary(reply);
    }).catch((caught: Error) => {
      if (!cancelled) setFailed(caught.message);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [upload, outcome, weightedMsrp, coreShare]);
  const sections = useMemo(() => {
    if (!summary) return [];
    return ['CALL', 'RESULT', 'NEXT'].map(label => {
      const match = summary.match(new RegExp(`${label}:?\\s*([\\s\\S]*?)(?=\\n\\s*(?:CALL|RESULT|NEXT):|$)`, 'i'));
      return {
        label,
        text: match?.[1]?.trim() ?? ''
      };
    }).filter(section => section.text.length > 0);
  }, [summary]);
  const labels: Record<string, string> = {
    CALL: 'The call',
    RESULT: 'The result',
    NEXT: 'What to do next'
  };
  return <section className="mt-4 rounded-xl border border-[#BB5E40]/25 bg-[#BB5E40]/5 p-5" aria-label="Carli’s read" data-id="element-2096" data-mp="qx78rjdxl25x">
      <div className="flex items-center gap-2" data-id="element-2097" data-mp="vw0n1e11kx75">
        <SparklesIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-2098" data-mp="vv8r223j8xgc" />
        <h3 className="text-sm font-semibold" data-id="element-2099" data-mp="zywy8jo95r12">Carli’s read</h3>
      </div>

      {loading && <p className="mt-3 flex items-center gap-2 text-xs text-ink/55" data-id="element-2100" data-mp="14wfc8oh4mwfx">
          <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-2101" data-mp="19vrz398003ii" />
          Comparing the forecast against actual sales…
        </p>}

      {failed && !loading && <p className="mt-3 text-xs leading-relaxed text-ink/60" data-id="element-2102" data-mp="1jmmmigwzd95j">{failed}</p>}

      {sections.length > 0 && <dl className="mt-3 grid gap-4 lg:grid-cols-3" data-id="element-2103" data-mp="1nohlvfvcpcgo">
          {sections.map(section => <div key={section.label} data-id="element-2104" data-mp="1sm15kvatihg0">
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2105" data-mp="1sn2nnjq94478">{labels[section.label.toUpperCase()] ?? section.label}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-ink/75" data-id="element-2106" data-mp="385h91utn6z1t">{section.text}</dd>
            </div>)}
        </dl>}

      {!loading && !failed && sections.length === 0 && summary && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink/75" data-id="element-2107" data-mp="2z63yeyts84fi">{summary}</p>}
    </section>;
}
function Composition({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [open, setOpen] = useState(false);
  const recoveryRate = upload.msrp > 0 ? Math.round(upload.estimatedRecovery / upload.msrp * 100) : 0;
  return <section className="mt-4 overflow-hidden rounded-xl border border-ink/10 bg-paper" aria-label="Assumptions and composition" data-id="element-2108" data-mp="jdoa64s7f0c8">
      <button type="button" onClick={() => setOpen(current => !current)} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-cream/45" data-id="element-2109" data-mp="em6ffygrxvzp">
        <span data-id="element-2110" data-mp="or4qdl56jl9u">
          <span className="block text-sm font-semibold text-ink" data-id="element-2111" data-mp="orejvipfoaka">How the forecast was built</span>
          <span className="mt-0.5 block text-xs text-ink/50" data-id="element-2112" data-mp="sshthngw1ad2">
            {perUnit(weightedMsrp)} weighted MSRP · {recoveryRate}% modeled recovery · {coreShare}% core sizes · {count(upload.lineItems)} variant lines
          </span>
        </span>
        <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-2113" data-mp="jtmogepky6bc" />
      </button>

      {open && <div className="border-t border-ink/10 px-5 py-5" data-id="element-2114" data-mp="9unfev0wk8nt">
          <ol className="grid gap-3 lg:grid-cols-4" data-id="element-2115" data-mp="206l02jiefcyx">
            <WaterfallStep label="Weighted MSRP" value={perUnit(weightedMsrp)} detail="Retail value per unit across the lot." data-id="element-2116" data-mp="206tckaam0g9l" />
            <WaterfallStep label="A-grade promo price" value={perUnit(weightedMsrp * 0.7)} detail="Nisolo was running ~30% off new product." data-id="element-2117" data-mp="245kcep6gmc0e" />
            <WaterfallStep label="Predicted recovery" value={perUnit(outcome.predictedAur)} detail={`${recoveryRate}% of MSRP after condition and demand.`} data-id="element-2118" data-mp="3kpdmvtsvyjik" />
            <WaterfallStep label="Actual recovery" value={outcome.matched ? perUnit(outcome.actualAur) : '—'} detail={outcome.matched ? `${signedPct(outcome.aurVariancePct)} against the forecast.` : 'Pending first matched sales.'} highlight data-id="element-2119" data-mp="2c7d63v9p1hf" />
          </ol>

          <div className="mt-4 grid gap-3 lg:grid-cols-3" data-id="element-2120" data-mp="l9qr9lwl1aap">
            <Distribution title="Sizes" items={upload.sizes.slice(0, 8)} total={upload.received} tiered data-id="element-2121" data-mp="l74ijixygoqw" />
            <Distribution title="Categories" items={upload.categories.slice(0, 8)} total={upload.received} data-id="element-2122" data-mp="g8b7rya1zbuf" />
            <Distribution title="MSRP bands" items={upload.priceBands} total={upload.received} data-id="element-2123" data-mp="2i1il5263vjdo" />
          </div>

          <div className="mt-4 grid gap-4 border-t border-ink/10 pt-4 lg:grid-cols-2" data-id="element-2124" data-mp="1x62yryopjodp">
            <div data-id="element-2125" data-mp="217enm1zrc5ku">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2126" data-mp="216wgubfgy8yo">Confidence: medium — what we knew</p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/65" data-id="element-2127" data-mp="25zfvlbpw7a52">
                Unit counts, MSRP, size mix, and the {upload.grade} designation were known. {count(upload.lineItems - upload.missingPredict)} of {count(upload.lineItems)} lines carried a per-unit estimate, and {count(upload.shortShipped)} invoiced units never arrived.
              </p>
            </div>
            <div data-id="element-2128" data-mp="1n7dmclfkyw4u">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2129" data-mp="1n6vk9868lnp0">What we had to estimate</p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/65" data-id="element-2130" data-mp="1s81fbcnddn1p">
                Demand by style, liquidity by size, and how consistently the {upload.grade} condition would land with customers — the network had no prior Nisolo sell-through to calibrate against.
              </p>
            </div>
          </div>
        </div>}
    </section>;
}
function WaterfallStep({
  label,
  value,
  detail,
  highlight
}: {
  label: string;
  value: string;
  detail: string;
  highlight?: boolean;
}) {
  return <li className={`rounded-xl border p-4 ${highlight ? 'border-[#BB5E40] bg-[#BB5E40]/6' : 'border-ink/10 bg-cream/40'}`} data-id="element-2131" data-mp="i6nj1xnqkfkv">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2132" data-mp="98256ft7loqf">{label}</p>
      <p className={`mt-1.5 font-display text-xl font-semibold tracking-tight ${highlight ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-2133" data-mp="48yx0mhxn2e8">{value}</p>
      <p className="mt-1.5 text-xs leading-relaxed text-ink/55" data-id="element-2134" data-mp="3vegxjzeku9vv">{detail}</p>
    </li>;
}
function Distribution({
  title,
  items,
  total,
  tiered
}: {
  title: string;
  items: Array<{
    label: string;
    units: number;
    tier?: SizeTier;
  }>;
  total: number;
  tiered?: boolean;
}) {
  const max = Math.max(...items.map(item => item.units), 1);
  const tone: Record<SizeTier, string> = {
    Core: 'bg-[#BB5E40]',
    Adjacent: 'bg-[#C9A04E]',
    Tail: 'bg-ink/25'
  };
  return <article data-id="element-2135" data-mp="2i17zrqlx894c">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-2136" data-mp="2d0agtbt37b1s">{title}</p>
      <ul className="mt-2.5 space-y-1.5" data-id="element-2137" data-mp="289kfc9ns7scd">
        {items.map(item => <li key={item.label} className="flex items-center gap-2.5" data-id="element-2138" data-mp="287r38euegfwz">
            <span className="w-20 shrink-0 truncate text-xs text-ink/65" data-id="element-2139" data-mp="247797k54hsf2">{item.label}</span>
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink/8" data-id="element-2140" data-mp="1z9xa1crxm2e1">
              <span className={`block h-full rounded-full ${tiered ? tone[item.tier ?? 'Tail'] : 'bg-[#BB5E40]'}`} style={{
            width: `${Math.max(2, item.units / max * 100)}%`
          }} data-id="element-2141" data-mp="1u99jes7m7nkc" />
            </span>
            <span className="w-9 shrink-0 text-right text-xs tabular-nums text-ink/55" data-id="element-2142" data-mp="1l9mhm5az4fqr">{total > 0 ? Math.round(item.units / total * 100) : 0}%</span>
          </li>)}
      </ul>
    </article>;
}
function buildContext(upload: NisoloSubmission, outcome: ReturnType<typeof reconcileSubmission>, weightedMsrp: number, coreShare: number, instructions: string[]) {
  return [`Inventory submission: "${upload.name}" (${upload.grade}), uploaded ${upload.arrived}, brand Nisolo, sold through the Manymoons recovery network.`, `Received ${upload.received} units across ${upload.lineItems} variant lines. ${upload.shortShipped} invoiced units never arrived.`, `Submitted MSRP value ${currency(upload.msrp)} (${perUnit(weightedMsrp)} weighted per unit). Nisolo A-grade was promoting at ~30% off, so first-quality price sat near ${perUnit(weightedMsrp * 0.7)}.`, `FORECAST: ${currency(upload.estimatedRecovery)} total, ${perUnit(outcome.predictedAur)} per unit received.`, outcome.matched ? `ACTUAL SO FAR: ${outcome.soldUnits} units sold for ${currency(outcome.actualSales)} (${perUnit(outcome.actualAur)} AUR). Those same units were forecast at ${currency(outcome.predictedForSold)}, so variance is ${currency(outcome.variance)} (${signedPct(outcome.variancePct)}). AUR variance ${signedPct(outcome.aurVariancePct)}. Sell-through ${outcome.sellThrough.toFixed(1)}%. ${outcome.unsoldUnits} units remain, carrying ${currency(outcome.remainingPredicted)} of remaining forecast.` : 'ACTUAL SO FAR: no sales have been matched to this upload yet, so there is no realized variance.', outcome.overPerformers.length > 0 ? `Beat forecast: ${outcome.overPerformers.map(item => `${item.name} (${item.soldUnits} of ${item.receivedUnits} sold, ${perUnit(item.actualAur)} vs ${perUnit(item.predict)} predicted)`).join('; ')}.` : '', outcome.underPerformers.length > 0 ? `Missed forecast: ${outcome.underPerformers.map(item => `${item.name} (${item.soldUnits} of ${item.receivedUnits} sold, ${perUnit(item.actualAur)} vs ${perUnit(item.predict)} predicted)`).join('; ')}.` : '', `Composition: ${coreShare}% of units in core-demand sizes. Categories: ${upload.categories.slice(0, 6).map(item => `${item.label} (${item.units})`).join(', ')}. MSRP bands: ${upload.priceBands.map(item => `${item.label} (${item.units})`).join(', ')}.`, '', ...instructions].filter(Boolean).join('\n');
}
function ReportChat({
  upload,
  outcome,
  weightedMsrp,
  coreShare
}: {
  upload: NisoloSubmission;
  outcome: ReturnType<typeof reconcileSubmission>;
  weightedMsrp: number;
  coreShare: number;
}) {
  const [messages, setMessages] = useState<Array<{
    id: number;
    role: 'user' | 'assistant';
    text: string;
  }>>([]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const suggestions = outcome.matched ? ['Why did the forecast miss?', 'What should we reprice?', 'How should we clear what’s left?'] : ['What should launch first?', 'How should we price this?', 'What are the biggest risks?'];
  const send = async (question: string) => {
    if (!question.trim() || thinking) return;
    const history: AgentMessage[] = [...messages.map(message => ({
      role: message.role,
      content: message.text
    })), {
      role: 'user',
      content: question
    }];
    setMessages(current => [...current, {
      id: Date.now(),
      role: 'user',
      text: question
    }]);
    setDraft('');
    setThinking(true);
    try {
      const reply = await askCarli({
        messages: history,
        context: buildContext(upload, outcome, weightedMsrp, coreShare, ['Answer only from this submission data. Be specific and commercial. Two short paragraphs maximum.']),
        maxTokens: 600
      });
      setMessages(current => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: reply
      }]);
    } catch (caught) {
      setMessages(current => [...current, {
        id: Date.now() + 1,
        role: 'assistant',
        text: (caught as Error).message
      }]);
    } finally {
      setThinking(false);
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void send(draft.trim());
  };
  return <section className="mt-4" aria-label="Ask Carli about this report" data-id="element-2143" data-mp="3l9g7qa9mql6e">
      {messages.length > 0 && <ul className="mb-3 max-h-56 space-y-2 overflow-y-auto pr-1" aria-live="polite" data-id="element-2144" data-mp="20rnuk60lmnpt">
          {messages.map(message => <li key={message.id} className={message.role === 'user' ? 'flex justify-end' : ''} data-id="element-2145" data-mp="25r0iku37i0tz">
              <p className={`max-w-2xl whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${message.role === 'user' ? 'bg-ink text-cream' : 'bg-cream/70 text-ink/75'}`} data-id="element-2146" data-mp="29r0vubekk5zg">{message.text}</p>
            </li>)}
        </ul>}

      {thinking && <p className="mb-2 flex items-center gap-2 text-xs text-ink/55" data-id="element-2147" data-mp="2ofxdn06fgxd9">
          <LoaderCircleIcon className="h-3.5 w-3.5 animate-spin text-[#BB5E40]" aria-hidden="true" data-id="element-2148" data-mp="2tfte4pfx26o2" />
          Carli is reviewing this upload…
        </p>}

      <form onSubmit={submit} className="flex items-center gap-2 rounded-xl border border-ink/12 bg-cream/50 p-1.5 pl-3.5" data-id="element-2149" data-mp="2xglrnuvtmxq5">
        <label htmlFor={`report-chat-${upload.id}`} className="sr-only" data-id="element-2150" data-mp="z44z6n9oq43o">Ask Carli about {upload.name}</label>
        <input id={`report-chat-${upload.id}`} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Ask Carli about this upload…" className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink/40" data-id="element-2151" data-mp="26e9s9yxcr822" />
        <button type="submit" disabled={!draft.trim() || thinking} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40] text-cream transition-colors hover:bg-[#94452E] disabled:cursor-not-allowed disabled:bg-ink/15" aria-label="Send question" data-id="element-2152" data-mp="2bdmeqikbad44">
          {thinking ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-2153" data-mp="2z1ni7d2mv3co" /> : <SendHorizontalIcon className="h-4 w-4" data-id="element-2154" data-mp="2u443kc20wok7" />}
        </button>
      </form>

      {messages.length === 0 && <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-2155" data-mp="382ve5ohofs3l">
          {suggestions.map(item => <button key={item} type="button" onClick={() => void send(item)} className="rounded-full border border-ink/12 px-3 py-1.5 text-xs font-medium text-ink/65 transition-colors hover:border-[#BB5E40]/45 hover:text-[#94452E]" data-id="element-2156" data-mp="335bnfp3slmur">
              {item}
            </button>)}
        </div>}
    </section>;
}
function CarliMark() {
  return <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40]/10" data-id="element-2157" data-mp="3hpo5dew77rom">
      <span className="bg-[#BB5E40]" style={{
      width: '1.125rem',
      height: '1.125rem',
      WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
      maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-2158" data-mp="33xrgdjpttxwg" />
    </span>;
}